import { SyncOperationText } from "@/components/sync/sync-operation-text";
import { Optional } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { AppStore } from "@/store";
import { FavoriteItemObservable } from "@/store/favorite-items/FavoriteItemObservable";
import {
  FavoriteItemModelData,
  FavoriteItemUpsertedSyncUpdateValue,
} from "@/store/favorite-items/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IAddCollectionToFavoritesOperation } from "@/store/sync/operations/types";
import {
  OptimisticSyncUpdate,
  SyncCustomErrorData,
  SyncModelData,
  SyncOperationKind,
} from "@/store/sync/types";
import { generateKeyBetween } from "fractional-indexing";

export class AddCollectionToFavoritesOperation extends BaseSyncOperation<IAddCollectionToFavoritesOperation> {
  private favoriteItem?: FavoriteItemObservable;

  get operationKind(): SyncOperationKind {
    return "ADD_COLLECTION_TO_FAVORITES";
  }

  get successToastMessage() {
    return <>Pinned {this.mediumTitle} to sidebar</>;
  }

  get title() {
    return this.store.collections.get(this.payload.collection_id)?.label;
  }

  get mediumTitle() {
    return <SyncOperationText>{this.title}</SyncOperationText>;
  }

  get favoriteItemId() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;

    return uuidModule.resolveFavoriteItemSyncModelUuid({
      spaceAccountId,
      itemId: this.payload.collection_id,
    });
  }

  // Need to override constructor to make sort_key optional
  constructor({
    store,
    payload: providedPayload,
    operationId: providedOperationId,
  }: {
    payload: Optional<IAddCollectionToFavoritesOperation["payload"], "schema_version" | "sort_key">;
    operationId?: Uuid;
    store: AppStore;
  }) {
    const lastItemSortKey = store.favoriteItems.sortedFavoriteItems.at(-1)?.sortKey || null;
    const payload = {
      ...providedPayload,
      sort_key: providedPayload.sort_key ?? generateKeyBetween(lastItemSortKey, null),
      schema_version: providedPayload.schema_version ?? 1,
    };

    super({ store, payload, operationId: providedOperationId || uuidModule.generate() });
  }

  async execute() {
    this.favoriteItem = await this.store.favoriteItems.getAsync(this.favoriteItemId);

    await super.execute();
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const syncUpdate = this.generateOptimisticFavoriteItemUpsertedUpdate();

    return [syncUpdate];
  }

  async triggerRecompute() {
    await this.store.favoriteItems.recompute(this.favoriteItemId);
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.triggerToast(this.getToastMessage(), SyncErrorHandlingType.RetryWithLimit);
  }

  protected getToastMessage() {
    return (
      <>
        {this.mediumTitle} could not be pinned to the sidebar. If this error continues, please
        contact support.
      </>
    );
  }

  protected generateOptimisticFavoriteItemUpsertedUpdate() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const favoriteItem = this.favoriteItem;

    const value: FavoriteItemUpsertedSyncUpdateValue = {
      model_id: this.favoriteItemId,
      model_kind: "FAVORITE_ITEM",
      model_version: favoriteItem?.modelVersion ?? 0,
      model_data: {
        space_account_id: spaceAccountId,
        item_id: this.payload.collection_id,
        item_kind: "COLLECTION",
        locally_created_at: this.committedAt,
        locally_modified_at: this.committedAt,
        sort_key: this.payload.sort_key,
      },
      model_scopes: favoriteItem
        ? favoriteItem.modelScopes
        : [generateDefaultOwnerScopes({ store: this.store })],
    };

    const syncUpdate: OptimisticSyncUpdate<FavoriteItemModelData> = {
      optimistic_update_id: uuidModule.generate(),

      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };

    return syncUpdate;
  }
}
